<template>
  <div
    class="calc-range range control"
    :data-field="externalKey"
  >
    <label
      v-if="label"
      class="range__label"
    >
      {{ label }}{{ measureUnit }}
    </label>
    <control-text
      :is-disabled="inputDisabled"
      :default-value="value"
      :default-display-value="monthToYearsValue"
      :external-handler="inputHandler"
      :external-change-handler="changeInputHandler"
      :external-key="inputExternalKey"
    />
    <div
      id="range-slider"
      ref="slider"
      class="range__slider"
    />
  </div>
</template>

<script>
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';
import ControlText from '@ui/components/ControlText';

export default {
  name: 'ControlRange',
  components: {
    ControlText
  },
  props: {
    externalKey: {
      type: String,
      default: ''
    },
    externalHandler: {
      type: Function,
      default: () => {}
    },
    label: {
      type: String,
      default: ''
    },
    measureUnitTitle: {
      type: String,
      default: ''
    },
    params: {
      type: Object,
      default: () => ({
        step: 1,
        range: {
          min: 1,
          max: 10
        }
      })
    },
    defaultValue: {
      type: Number,
      default: 0
    },
    inputDisabled: {
      type: Boolean,
      default: false
    },
    // for terms range
    isMonthToYears: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      slider: null,
      value: this.defaultValue,
      setByInput: false,
      isInputActive: false,
      moneyFormat: wNumb({
        thousand: ' '
      })
    };
  },
  computed: {
    inputExternalKey(){
      return this.externalKey ? this.externalKey + '.input' : '';
    },
    valueToNumber() {
      return parseInt(this.value);
    },
    measureUnit() {
      return this.measureUnitTitle ? ', ' + this.measureUnitTitle : '';
    },
    isIntermediate() {
      return this.params.pips.values.length > 2;
    },
    rangeMin() {
      const { min: rangeMin } = this.params.range;

      return Array.isArray(rangeMin) ? Math.max(...rangeMin) : rangeMin;
    },
    rangeMax() {
      const { max: rangeMax } = this.params.range;

      return Array.isArray(rangeMax) ? Math.max(...rangeMax) : rangeMax;
    },
    monthToYearsValue() {
      return this.isMonthToYears ? this.monthToYears(this.value) : '';
    }
  },
  watch: {
    params() {
      this.slider.updateOptions({
        start: this.params.range.min,
        range: this.params.range,
        // TODO: think of set pips format at one place
        pips: { ...this.params.pips }
      });
      this.initSliderValues();
    }
  },
  mounted() {
    const slider = this.$refs.slider;
    noUiSlider.create(slider, this.getSliderSettings());
    this.slider = slider.noUiSlider;
    this.slider.on('update', this.changeSliderHandler.bind(this));
    this.initSliderValues();

    if (this.rangeMin === 0) {
      slider.setAttribute('disabled', true);
    }
  },
  methods: {
    /**
    * Get settings for slider
    * @return {object} - settings, object with slider settings
    */
    getSliderSettings() {
      const settings = {
        start: this.defaultValue || this.rangeMin,
        connect: [true, false],
        range: this.params.range,
        pips: this.params.pips,
        density: 100,
        step: this.params.step
      };

      if(this.isMonthToYears){
        settings.pips.format = this.pipsWithYears(this.params.pips.values);
      }

      settings.snap = this.params.snap ? this.params.snap : false;

      return settings;
    },
    /**
    * Slider initial state setup
    */
    initSliderValues() {
      let valueElements = this.$refs.slider.getElementsByClassName('noUi-value');

      for (let valueElement of valueElements) {
        valueElement.onclick = () => {
          const value = valueElement.dataset.value;

          this.slider.set(valueElement.dataset.value);
          this.value = value;
          this.externalHandler({ key: this.externalKey, val: this.valueToNumber });
        };
      }
    },
    /**
    * Handles slider change
    * @param {array} - values, an array of changed values (in our case it's only one value in array)
    */
    changeSliderHandler(values) {
      if (this.setByInput) {
        // to prevent change slider value after user input interaction bug
        this.setByInput = false;
        return false;
      }
      this.value = parseInt(values[0]);
      this.externalHandler({ key: this.externalKey, val: this.valueToNumber });
    },
    /**
    * Handles input event of ControlText input
    * @param {object} - val, user input value
    */
    inputHandler({ val }) {
      const isNumeric = (/^\d+$/gi).test(val);

      this.setByInput = true;
      this.value = val;

      if ((!isNumeric && val !== '') || this.valueToNumber > 1e+14) {
        this.value = this.rangeMin;
      }

      const validValue = this.getValidRangeValueStrategy(this.value === '' ? 0 : this.valueToNumber);

      this.externalHandler({ key: this.externalKey, val: validValue });
      this.slider.set(this.valueToNumber);
    },
    /**
    * Handles change event of ControlText input
    */
    changeInputHandler() {
      this.value = this.getValidRangeValueStrategy(this.value === '' ? 0 : this.valueToNumber);
      this.setByInput = false;
    },
    /**
    * Get valid value in case when value overflows of range
    * @param {object} - value, valueMin, valueMax
    * @return {number} - valid value
    */
    getValidRangeValue({ value, valueMin, valueMax }) {

      if (value < valueMin) {
        return valueMin;
      } else if (value > valueMax) {
        return valueMax;
      }

      return value;
    },
    /**
    * Get valid value in case when value not included in provided set of values
    * @param {object} - value, valueMin, valueMax, values
    * @return {number} - valid value
    */
    getValidRangeValueWithIntermediate({ value, valueMin, valueMax, values }) {
      const firstTry = this.getValidRangeValue({ value, valueMin, valueMax });

      if (!values.includes(firstTry)) {
        return valueMin;
      }

      return firstTry;
    },
    /**
    * Stratrgy method conditionally uses one of getValidRangeValueWithIntermediate or getValidRangeValue methods
    * @param {number} - val
    * @return {number} - valid value
    */
    getValidRangeValueStrategy(val) {
      return this.isIntermediate && this.inputDisabled ? 
        this.getValidRangeValueWithIntermediate({ 
          value: val, 
          valueMin: this.rangeMin, 
          valueMax: this.rangeMax, 
          values: this.params.pips.values 
        }) : 
        this.getValidRangeValue({ 
          value: val, 
          valueMin: this.rangeMin, 
          valueMax: this.rangeMax 
        });
    },
    /**
    * Retrieves needed form of month/year word depends on number provided
    * @param {number} - number of entities
    * @param {object} - word, hash with all existing word forms
    * @return {string} - correct word form
    */
    getPlural(number, word) {
      if (number === 1) {
        return word.one;
      } else if (number < 5) {
        return word.other;
      } else {
        return word.another;
      }
    },
    /**
    * Transforms given number of months to form X years Y months
    * @param {number} - monthCount, number of months
    * @return {string} - string in correct form
    */
    monthToYears(monthCount) {
      const monthsMap = { one: 'месяц', other: 'месяца', another: 'месяцев' };
      const yearsMap = { one: 'год', other: 'года', another: 'лет' };
      const monthsRemainder = monthCount % 12;
      const yearsCount = Math.floor(monthCount / 12);
      let result = [];

      yearsCount && result.push(yearsCount + ' ' + this.getPlural(yearsCount, yearsMap));
      monthsRemainder && result.push(monthsRemainder + ' ' + this.getPlural(monthsRemainder, monthsMap));

      return result.join(' и ');
    },
    pipsWithYears(pips) {

      Object.keys(pips).forEach(key => {
        const value = pips[key];
        pips[value] = this.monthToYears(value);
      });

      return {
        to: function (key) {
          return pips[key];
        }
      };
    }
  }
};
</script>

<style lang="scss">
  .calc-range {
    .noUi-target,
    .noUi-target * {
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      -webkit-user-select: none;
      -ms-touch-action: none;
      touch-action: none;
      -ms-user-select: none;
      -moz-user-select: none;
      user-select: none;
      -moz-box-sizing: border-box;
      box-sizing: border-box;
    }

    .noUi-target {
      position: relative;
      direction: ltr;
      top: -2px;
      border: none;
      box-shadow: none;
    }

    .noUi-base,
    .noUi-connects {
      width: 100%;
      height: 6px;
      position: relative;
      z-index: 1;
    }

    /* Wrapper for all connect elements.
      */
    .noUi-connects {
      overflow: hidden;
      z-index: 0;
      background-color: #d9d9d9;
    }

    .noUi-connect,
    .noUi-origin {
      will-change: transform;
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      -ms-transform-origin: 0 0;
      -webkit-transform-origin: 0 0;
      transform-origin: 0 0;
    }

    /* Offset direction
      */
    .noUi-horizontal .noUi-origin {
      left: auto;
      right: 0;
    }

    /* Give origins 0 height/width so they don't interfere with clicking the
      * connect elements.
      */
    .noUi-vertical .noUi-origin {
      width: 0;
    }

    .noUi-horizontal .noUi-origin {
      height: 0;
    }

    .noUi-handle {
      position: absolute;
      transition-duration: 200ms;
    }

    .noUi-handle:hover {
      border-width: 1px;
      border-color: $color-black;
    }

    .noUi-state-tap .noUi-connect,
    .noUi-state-tap .noUi-origin {
      -webkit-transition: transform 0.3s;
      transition: transform 0.3s;
    }

    .noUi-state-drag * {
      cursor: inherit !important;
    }

    /* Slider size and handle placement;
      */
    .noUi-horizontal {
      height: 4px;
    }

    .noUi-horizontal .noUi-handle {
      width: 30px;
      height: 30px;
      left: -32px;
      top: -9px;
      transition: none;
      box-sizing: border-box;

      &.noUi-active {
        width: 30px;
        height: 30px;
        right: -15px;
        top: -12px;
      }
    }

    .noUi-vertical {
      width: 18px;
    }

    .noUi-vertical .noUi-handle {
      width: 28px;
      height: 34px;
      left: -6px;
      top: -17px;
    }

    .noUi-horizontal .noUi-handle {
      right: -15px;
      top: -12px;
      left: auto;
      outline: none;
      cursor: pointer;
    }

    /* Styling;
      * Giving the connect element a border radius causes issues with using transform: scale
      */
    .noUi-target {
      border: none;
    }

    .noUi-connect {
      background: $color-yellow;
    }

    /* Handles and cursors;
      */
    .noUi-draggable {
      cursor: ew-resize;
    }

    .noUi-vertical .noUi-draggable {
      cursor: ns-resize;
    }

    .noUi-handle {
      border: 1px solid #bfbfbf;
      border-radius: 50%;
      background: #FFF;
      cursor: default;

      &:before {
        width: 45%;
        height: 45%;
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: $color-yellow;
        border-radius: 100%;
      }
    }

    .noUi-handle:after {
      left: 17px;
    }

    .noUi-vertical .noUi-handle:before,
    .noUi-vertical .noUi-handle:after {
      width: 14px;
      height: 1px;
      left: 6px;
      top: 14px;
    }

    .noUi-vertical .noUi-handle:after {
      top: 17px;
    }

    /* Disabled state;
    */
    [disabled] .noUi-connect {
      background: #B8B8B8;
    }

    [disabled].noUi-target,
    [disabled].noUi-handle,
    [disabled].noUi-handle {
      cursor: not-allowed;
    }

    /* Base;
    *
    */
    .noUi-pips,
    .noUi-pips * {
      -moz-box-sizing: border-box;
      box-sizing: border-box;
    }

    .noUi-pips {
      position: absolute;
      color: $color-black;
    }

    /* Values;
    *
    */
    .noUi-value {
      cursor: pointer;
      position: absolute;
      white-space: nowrap;
      text-align: center;
    }

    .noUi-value-sub {
      color: #ccc;
      font-size: 10px;
    }

    /* Markings;
    *
    */
    .noUi-marker {
      position: absolute;
      background: #CCC;
      display: none;
    }

    .noUi-marker-sub {
      background: #AAA;
    }

    .noUi-marker-large {
      background: #d6d6d6;
      display: block;
      height: 2px;
    }

    /* Horizontal layout;
    *
    */
    .noUi-pips-horizontal {
      margin-top: 6px;
      height: 40px;
      top: 100%;
      left: 0;
      width: 100%;
    }

    .noUi-pips-horizontal div:nth-child(2) {
      -webkit-transform: translate(0%, 50%);
      transform: translate(0%, 50%);
    }

    .noUi-pips-horizontal .noUi-value-horizontal:last-child {
      -webkit-transform: translate(-100%, 50%);
      transform: translate(-100%, 50%);
    }

    .noUi-value-horizontal {
      -webkit-transform: translate(-50%, 50%);
      transform: translate(-50%, 50%);
      margin-top: 3px;
    }

    .noUi-rtl .noUi-value-horizontal {
      -webkit-transform: translate(50%, 50%);
      transform: translate(50%, 50%);
    }

    .noUi-marker-horizontal.noUi-marker {
      margin-left: -1px;
      width: 1px;
      height: 8px;
    }

    .noUi-marker-horizontal.noUi-marker-sub {
      height: 10px;
    }

    .noUi-pips.noUi-pips-horizontal .noUi-marker-large:first-child {
      margin-left: 0;
    }

    /* Vertical layout;
    *
    */
    .noUi-pips-vertical {
      padding: 0 10px;
      height: 100%;
      top: 0;
      left: 100%;
    }

    .noUi-value-vertical {
      -webkit-transform: translate(0, -50%);
      transform: translate(0, -50%, 0);
      padding-left: 25px;
    }

    .noUi-rtl .noUi-value-vertical {
      -webkit-transform: translate(0, 50%);
      transform: translate(0, 50%);
    }

    .noUi-marker-vertical.noUi-marker {
      width: 5px;
      height: 2px;
      margin-top: -1px;
    }

    .noUi-marker-vertical.noUi-marker-sub {
      width: 10px;
    }

    .noUi-marker-vertical.noUi-marker-large {
      width: 15px;
    }

    .noUi-tooltip {
      display: block;
      position: absolute;
      border: 1px solid #D9D9D9;
      background: $color-white;
      color: $color-black;
      padding: 5px;
      text-align: center;
      white-space: nowrap;
    }

    .noUi-horizontal .noUi-tooltip {
      -webkit-transform: translate(-50%, 0);
      transform: translate(-50%, 0);
      left: 50%;
      bottom: 120%;
    }

    .noUi-vertical .noUi-tooltip {
      -webkit-transform: translate(0, -50%);
      transform: translate(0, -50%);
      top: 50%;
      right: 120%;
    }

    @include media-breakpoint-down(md) {
      .noUi-horizontal .noUi-handle {
        width: 30px;
        height: 30px;
        right: -15px;
        top: -12px;

        &.noUi-active {
          width: 30px;
          height: 30px;
          right: -15px;
          top: -12px;
        }
      }
    }
  }
</style>
<style lang="scss" scoped>
  .range {
    min-width: calcEm(100px);
    padding: 0 0 30px 0;
    position: relative;

    &__label {
      font-size: calcEm(16px);
      font-family: $font-family-medium;
    }

    &__slider {
      position: relative;
      z-index: 11;
    }
  }
</style>
